import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { UserChartChanged } from '@/domain/events/UserChartChanged'
import { UserChart } from '@/domain/models/UserChart'
import { Result } from '@/domain/protocols/Result'
import { ConvertedChartData } from '@/domain/protocols/UserChart'

import { IUserChartRepository, UserChartPendingChanges } from './IUserChartRepository'
import { clearAllPendingProcessedUploads, clearPendingUpload } from './userChartTilesOutbox'
import { clearAllUserChartTiles, clearUserChartTiles } from './userChartTileStore'

import type { IChartConverterWebClient } from '@/infra/http/ChartConverterWebClient/IChartConverterWebClient'
import { ConvertChartRequest } from '@/infra/http/ChartConverterWebClient/IChartConverterWebClient'

@injectable()
export class UserChartRepository implements IUserChartRepository {
  private userCharts: UserChart[] = []

  constructor(
    @inject(InjectionTokens.ChartConverterWebClient) private chartConverterWebClient: IChartConverterWebClient
  ) {}

  async init(): Promise<Result<void>> {
    this.userCharts = []
    return Result.ok()
  }

  async getCharts(): Promise<Result<UserChart[]>> {
    return Result.ok(this.userCharts.filter((chart) => !chart.deleted))
  }

  async getChart(id: UniqueEntityID): Promise<Result<UserChart>> {
    const chart = this.userCharts.find((c) => c.id.equals(id) && !c.deleted)
    if (!chart) {
      return Result.fail('Chart not found')
    }
    return Result.ok(chart)
  }

  async saveChart(chart: UserChart): Promise<Result<void>> {
    const index = this.userCharts.findIndex((c) => c.id.equals(chart.id))
    if (index >= 0) {
      this.userCharts[index] = chart
    } else {
      this.userCharts.push(chart)
    }
    UserChartChanged.next()
    return Result.ok()
  }

  async updateChart(chart: UserChart): Promise<Result<void>> {
    const index = this.userCharts.findIndex((c) => c.id.equals(chart.id))
    if (index < 0) {
      return Result.fail('Chart not found')
    }
    chart.synced = false
    chart.updatedAt = new Date().toISOString()
    this.userCharts[index] = chart
    UserChartChanged.next()
    return Result.ok()
  }

  async deleteChart(chart: UserChart): Promise<Result<void>> {
    const index = this.userCharts.findIndex((c) => c.id.equals(chart.id))
    if (index < 0) {
      return Result.fail('Chart not found')
    }
    const target = this.userCharts[index]
    target.deleted = true
    target.synced = false
    target.updatedAt = new Date().toISOString()
    UserChartChanged.next()
    return Result.ok()
  }

  async existsByName(originalFileName: string): Promise<Result<boolean>> {
    const normalized = originalFileName.toLowerCase()
    const exists = this.userCharts.some(
      (chart) => !chart.deleted && chart.originalFileName.toLowerCase() === normalized
    )
    return Result.ok(exists)
  }

  async convertChart(request: ConvertChartRequest): Promise<Result<ConvertedChartData>> {
    return this.chartConverterWebClient.convert(request)
  }

  getPendingChanges(): UserChartPendingChanges {
    return {
      upserts: this.userCharts.filter((chart) => !chart.synced && !chart.deleted),
      deletes: this.userCharts.filter((chart) => !chart.synced && chart.deleted)
    }
  }

  markSynced(ids: string[]): void {
    if (ids.length === 0) return
    const idSet = new Set(ids)
    this.userCharts.forEach((chart) => {
      if (idSet.has(chart.id.toString())) {
        chart.synced = true
      }
    })
  }

  hardDelete(ids: string[]): void {
    if (ids.length === 0) return
    const idSet = new Set(ids)
    this.userCharts = this.userCharts.filter((chart) => !idSet.has(chart.id.toString()))
    // Free the in-memory tiles (blob URLs) and drop any pending upload for the
    // removed charts.
    ids.forEach((id) => {
      clearUserChartTiles(id)
      // Drop the in-memory outbox entry (processed + original) for the removed chart.
      clearPendingUpload(id)
    })
  }

  clearLocalData(): void {
    this.userCharts = []
    clearAllUserChartTiles()
    clearAllPendingProcessedUploads()
  }
}
